import { Component, ElementRef, OnInit, ViewChild, AfterViewInit, OnDestroy } from "@angular/core";
import { AuthenticationService } from "src/app/authentications/authentication.service";
import { BubbleComponent } from "../../shared/components/bubble/bubble.component";
import {  ActivatedRoute, Router } from "@angular/router";
import { FsproStripeService } from "../../stripe-subscription/services/fspro-stripe.service";
import { Subject } from "rxjs";
import { NotificationsFacade } from "src/app/facades/notifications.facade";
import { takeUntil } from 'rxjs/operators';
import { UserType } from "../../shared/dtos/user-dto";

const authenticatedUrls = [
  '/my-invitations/list',
  '/rh/invitations/sent',
  '/rh/my-films',
  '/rh/booking-requests',
  '/rh/screening-reports',
  '/rh/screening-requests',
  '/rh/my-contacts',
  '/rh/invitation/autoselect/form',
  '/my-account/my-infos',
  '/my-feed',
  '/my-lists/watched',
  '/my-lists/to-watch',
  '/my-notes/list',
  '/my-bookings'
];

@Component({
  selector: 'fspro-header',
  templateUrl: './header.component.html',
  styleUrls: ['header.component.scss'],
  standalone: false
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('rubixBox', { read: BubbleComponent }) rubixBox: BubbleComponent;
  @ViewChild('myAccountBox', { read: BubbleComponent }) myAccountBox: BubbleComponent;
  @ViewChild('rubixToggle') rubixToggle: ElementRef;
  @ViewChild('myAccountBoxToggle') myAccountBoxToggle: ElementRef;

  public displayInviteTooltip: boolean = false;

  public isToggleMyAccount: boolean;
  public isUserRH: boolean = false;
  public stripePortalUrl: string | null = null;
  public isRestrictedToSinglePage: boolean = false;
  public displaySearchPopup: boolean = false;

  protected readonly UserType = UserType;

  private readonly _destroy$ = new Subject();

  get user() {
    return this.authentication.user;
  }

  get displayedFullname(): string {
    if (this.authentication.isImpersonating()) {
      return this.authentication.getTokenInfo(this.authentication.token)?.fullname;
    }

    return this.user?.fullname;
  }

  get isRenewOrUpgradeUser(): boolean {
    return this.user?.membershipUpdateStatus === 'renew' || this.user?.membershipUpdateStatus === 'upgrade';
  }

  constructor(
    public readonly authentication: AuthenticationService,
    public notificationsFacade: NotificationsFacade,
    private stripeService: FsproStripeService,
    private router: Router,
    private route: ActivatedRoute
  ) {
  }

  ngOnDestroy(): void {
    this._destroy$.next(null);
    this._destroy$.complete();
  }

  ngAfterViewInit(): void {
    document.addEventListener('click', this.handleOutsideClick.bind(this));
  }

  handleOutsideClick(event: MouseEvent) {
    const clickedInsideAccountBox = this.myAccountBoxToggle?.nativeElement?.contains(event.target);
    if (!clickedInsideAccountBox && this.myAccountBox?.isToggled) {
      this.myAccountBox.close();
    }

    const clickedInsiderubixToggle = this.rubixToggle?.nativeElement?.contains(event.target);
    if (!clickedInsiderubixToggle && this.rubixBox?.isToggled) {
      this.rubixBox.close();
    }
  };

  ngOnInit(): void {
    this.authentication.currentUser$.pipe(takeUntil(this._destroy$)).subscribe((user) => {
      if (user) {
        this.isRestrictedToSinglePage = user.shouldVerify || user.mustChangePassword;
        this.isUserRH = this.authentication.isRhUser();

        if (this.isUserRH) {
          this.notificationsFacade.loadPendingScreeningRequest();
          this.notificationsFacade.loadPendingBookingRequest();
          this.notificationsFacade.loadFilmAvailableForInvitation();
        }

        if (user.userType === UserType.USER) {
          this.stripeService.getPortalAddress().subscribe((url) => { this.stripePortalUrl = url.url });
        }
      }
    });
  }



  async onSignOut(): Promise<void> {
    this.isToggleMyAccount = false;
    await this.authentication.signOut();
    const currentRoute = this.router.routerState.snapshot.url;
    if (!authenticatedUrls.includes(currentRoute)) {
      window.location.reload();
    } else {
      window.location.replace('/');
    }
  }

  public displayInviteTooltipToggle(): void {
    this.displayInviteTooltip = true;
  }

  public displayInviteTooltipClose(): void {
    this.displayInviteTooltip = false;
  }
  public dismissSearch(){
    this.displaySearchPopup = false;
  }

  public goToLogin(): void {
    const currentUrl = this.router.url;
    const existingRedirect = this.route.snapshot.queryParamMap.get('redirect');
    const queryParams: any = {};
    if (existingRedirect) {
      queryParams.redirect = existingRedirect;
    } else if (currentUrl && currentUrl !== '/' && !currentUrl.startsWith('/sign-in')) {
      queryParams.redirect = currentUrl;
    }

    this.router.navigate(['/sign-in'], {
      queryParams
    });
  }
}
